import { useState } from 'react';
import { Newspaper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PlayerNewsDialog from './PlayerNewsDialog';
import { useIsNewsActive } from './ActiveNewsContext';
import { cn } from '@/lib/utils';

/**
 * SAB-1173: the badge now renders ONLY when the player has ACTIVE news.
 *
 * It used to render unconditionally at all 14 sites, so a blurb from March looked
 * identical to one from this morning, and clicking a player with no rows opened an
 * empty panel. `hasActiveNews` comes from useActiveNewsPlayers — ONE batched query
 * per list, never one per row; a per-row query on the draft board is the failure
 * mode that hook exists to prevent.
 *
 * THE INTERACTION MODEL DIFFERS BY SPORT, DELIBERATELY (Adam, 2026-09-14):
 *
 *   NFL  badge -> player card -> News tab. NFLPlayerDetailModal already has tabs, so
 *        the badge just asks its parent to open the card at `news`.
 *
 *   MLB  badge -> PlayerNewsDialog, as before.
 *
 * MLB does NOT route to a player card because its badge hosts DO NOT HAVE ONE.
 * MLBPlayerCardModal — which owns MLB's news tab — is rendered only by the mlb-beta
 * surfaces (MatchupBoxScore, MLBPlayerRow, MLBBetaMyTeam). The 14 badge sites live in
 * the older draft/trade/roster UI, which never opens a card. Wiring one into those is
 * a restructure hiding inside a badge change, and modal-inside-modal on trade flows is
 * its own problem.
 *
 * This asymmetry is a RECORDED DECISION, not an inconsistency awaiting cleanup. What
 * IS shared is the part that matters: one activeness predicate (playerNewsActive.ts)
 * and one list renderer (PlayerNewsList.tsx), so the two sports cannot drift on what
 * "news" means or how it looks.
 */
interface PlayerNewsIconProps {
  /** Omit to keep the legacy MLB behaviour. */
  sport?: 'mlb' | 'nfl';
  playerName: string;
  /** Required for NFL: nfl_players.id. */
  playerId?: string | null;
  /** From useActiveNewsPlayers. Undefined keeps the old always-on behaviour. */
  hasActiveNews?: boolean;
  /** NFL only — parent opens its player card at the News tab. */
  onOpenNews?: () => void;
  size?: 'sm' | 'md';
  className?: string;
}

const PlayerNewsIcon = ({
  sport = 'mlb',
  playerName,
  playerId,
  hasActiveNews,
  onOpenNews,
  size = 'sm',
  className,
}: PlayerNewsIconProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  // An explicit prop wins; otherwise fall back to the list-level provider. Rows that
  // are inside neither keep the legacy always-on behaviour rather than vanishing.
  const fromContext = useIsNewsActive(sport === 'nfl' ? playerId : playerName);
  const active = hasActiveNews ?? fromContext;
  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  // The whole point of SAB-1173: no active news, no affordance.
  if (active === false) return null;

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        aria-label={`News for ${playerName}`}
        // NFL: exempt from the global 44px button minimum (index.css, <=768px) and keep a 44x44
        // TAP TARGET with an invisible ::after instead. MEASURED on My Team at 390: the minimum
        // made this 27px icon take 44px + gap of a 91px name row, leaving "J. …". The rule is
        // right about tap targets and wrong about layout width; this keeps the first and drops
        // the second. MLB's legacy rows are left alone -- the halo there could overlap a
        // neighbouring control, which nobody has measured.
        data-compact={sport === 'nfl' ? '' : undefined}
        className={cn(
          'h-6 w-6 shrink-0 hover:bg-primary/10 hover:text-primary transition-colors',
          sport === 'nfl' && "relative after:absolute after:left-1/2 after:top-1/2 after:size-[44px] after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']",
          className,
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (sport === 'nfl') onOpenNews?.();
          else setDialogOpen(true);
        }}
      >
        <Newspaper className={iconSize} />
      </Button>

      {sport === 'mlb' && (
        <PlayerNewsDialog
          playerName={playerName}
          playerId={playerId ?? undefined}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      )}
    </>
  );
};

export default PlayerNewsIcon;
